import { useMemo, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { useQueryClient } from "@tanstack/react-query"
import { faLock } from "@fortawesome/pro-solid-svg-icons"
import { Grid, Typography, Tooltip, Stack } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser, faCircleEuro } from "@fortawesome/pro-light-svg-icons"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { getFilteredTableData } from "../../../../../../shared/utils/tableUtils"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import { MeetingServiceOrdersResponse } from "../../../../../merchant/merchant/production/meetingService/meetingServiceApi"
import {
    DeliveryType,
    MeetingServiceOrder,
} from "../../../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { fetchMeetingServiceForUser, UpcomingOrdersMeetingService } from "../meetingServiceApi"
import { DatePeriod } from "../../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { useDateTime } from "../../../../../localization/useDateTime"
import { MeetingCardMenu } from "../../../home/components/upcomingOrders/meetings/MeetingCardMenu"
import { MenuMeeting } from "../../../home/weekMenuTypes"
import { SimpleStatusChip } from "../../../../../merchant/merchant/production/meetingService/components/StatusMenu"
import { TimeOrDatePicker } from "../../../../../../shared/components/table/TimeOrDatePicker"
import {
    StandardSuccessDialog,
    SuccessDialogType,
} from "../../../../../../shared/components/dialogs/StandardSuccessDialog"
import { OrderChatIcon } from "../../../../../../shared/components/orderChat/OrderChatIcon"
import { OrderChatDrawer } from "../../../../../../shared/components/orderChat/OrderChatDrawer"
import { ChatMessageAuthorRole, UserType } from "../../../../../../shared/components/orderChat/orderChatTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId, selectEffectiveCompanyId } from "../../../../../context/contextSelectors"
import { UserMeetingsReceipt } from "../../../MeetingsReceipt/UserMeetingsReceipt"

const useStyles = makeStyles()((theme) => ({
    wrapIconText: {
        display: "flex",
        gap: "2px",
        alignItems: "center",
    },
    secondLine: {
        color: theme.palette.tailwind.neutral[500],
    },
}))

const selectMeetingService = (data: MeetingServiceOrdersResponse) => data?.orders || []

export function UserMeetingService() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const todayStart = dateTimeFactory.now().startOf("day")
    const queryClient = useQueryClient()
    const userId = useAppSelector(selectEffectiveUserId)
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const [chatDialogOrder, setChatDialogOrder] = useState<{ id: number; title: string } | null>(null)
    const [receiptOrderId, setReceiptOrderId] = useState<number | null>(null)

    const [datePeriod, setDatePeriod] = useState<DatePeriod>(DatePeriod.ALL)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [showSuccessDialog, setShowSuccessDialog] = useState<{
        open: boolean
        type?: SuccessDialogType
        ordersCount?: number
    }>({ open: false })

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState({
        startDate: startDate < todayStart ? todayStart : startDate,
        endDate: endDate && endDate < todayStart ? todayStart : endDate,
    })

    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)

    const meetingServiceResult = useApiQuery(fetchMeetingServiceForUser, {
        queryName: UpcomingOrdersMeetingService.UPCOMING_ORDERS_MEETING_SERVICE,
        dependencies: {
            period: isDatePicker ? undefined : datePeriod,
            startDate: isDatePicker ? dateState.startDate?.toISODate() : undefined,
            endDate: isDatePicker ? dateState.endDate?.toISODate() : undefined,
        },
        select: selectMeetingService,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceResult)

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("meeting-service-table-order-id-column"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("meeting-service-table-delivery-time-column"),
                    accessorKey: "deliveryDetails.time",
                },
                (row) => row.deliveryDetails.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-delivery-location-column"),
                accessorKey: "deliveryDetails",
                Cell: ({ row }) => {
                    const { id, meetingDetails, deliveryDetails, paymentDetails, messages } = row.original
                    const deliveryType = deliveryDetails.type
                    return (
                        <Grid container gap={1.25}>
                            <Grid container alignItems="center" gap={2} flexWrap="nowrap">
                                <Stack direction="row" alignItems="center" gap="1rem" component="span">
                                    <Typography variant="caption">{meetingDetails.meetingName}</Typography>
                                    {!!deliveryDetails.sittings && (
                                        <Typography variant="body2" className={classes.wrapIconText}>
                                            <FontAwesomeIcon icon={faUser} color={"#334155"} />
                                            {deliveryDetails.sittings}
                                        </Typography>
                                    )}
                                    <OrderChatIcon
                                        messages={messages}
                                        onClick={() => setChatDialogOrder({ id, title: meetingDetails.meetingName })}
                                    />
                                </Stack>
                                {paymentDetails?.isContantPayment && (
                                    <FontAwesomeIcon icon={faCircleEuro} color={"#334155"} />
                                )}
                                {deliveryDetails.isLocked && (
                                    <LocalizedStrict
                                        id="meeting-service-table-lock-column-locked"
                                        attrs={{ title: true }}
                                    >
                                        <Tooltip
                                            arrow
                                            title="The order has been locked by the merchant. Contact merchant for updates."
                                        >
                                            <FontAwesomeIcon icon={faLock} color={"#334155"} />
                                        </Tooltip>
                                    </LocalizedStrict>
                                )}
                            </Grid>

                            {deliveryType !== DeliveryType.DELIVERY ? (
                                <Grid
                                    container
                                    alignItems="center"
                                    gap={1.5}
                                    flexWrap="nowrap"
                                    className={classes.secondLine}
                                >
                                    <LocalizedEnum base="delivery-type-filter" enumValue={deliveryType}>
                                        <Typography variant="caption">{deliveryType}</Typography>
                                    </LocalizedEnum>
                                </Grid>
                            ) : deliveryDetails.location.name ? (
                                <LocalizedStrict
                                    id="meeting-service-table-meeting"
                                    vars={{ locationName: deliveryDetails.location.name }}
                                >
                                    <Typography variant="caption" className={classes.secondLine}>
                                        Delivery: {deliveryDetails.location.name}
                                    </Typography>
                                </LocalizedStrict>
                            ) : null}
                        </Grid>
                    )
                },
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("meeting-service-table-amount-column"),
                    id: "amount",
                },
                (row) => row.amount,
                true,
            ),
            columnFactory.statusColumn({
                header: l10n.getString("meeting-service-table-status-column"),
                Cell: ({ row }) => {
                    const statusDetails = row.original.deliveryDetails.statusDetails
                    return (
                        <Stack alignItems="center">
                            <SimpleStatusChip status={statusDetails.status} tooltipLabel={statusDetails.message} />
                        </Stack>
                    )
                },
                filterSelectOptions: [
                    {
                        value: "yes",
                        text: l10n.getString("meeting-service-table-lock-column-active-filter-yes"),
                    },
                    {
                        value: "no",
                        text: l10n.getString("meeting-service-table-lock-column-inactive-filter-no"),
                    },
                ],
            }),
        ],
        [l10n, columnFactory],
    )

    const handleChangeTimeOrDatePicker = () => {
        setIsDatePicker((prev) => !prev)
    }

    const mapMeetingServiceToMeeting = (booking: MeetingServiceOrder): MenuMeeting => {
        const { meetingName, startDateTime, endDateTime } = booking.meetingDetails

        return {
            id: booking.id,
            uid: booking.uid,
            orderSummary: booking.orderSummary,
            deliveryDetails: booking.deliveryDetails,
            organizers: booking.organizers,
            meetingDetails: {
                meetingName,
                startDateTime,
                endDateTime,
            },
        }
    }

    return (
        <>
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                onRowSelectionChange={setRowSelection}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <MeetingCardMenu
                        ignoreMenuContainer
                        menuOnApi
                        meetings={getFilteredTableData(tableInstanceRef)}
                        meeting={mapMeetingServiceToMeeting(row.original)}
                        handleMenuClose={closeMenu}
                        variant="meeting-service"
                        onChatOpen={(id, title) => {
                            setChatDialogOrder({ id, title })
                            closeMenu()
                        }}
                    />,
                ]}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <TimeOrDatePicker
                            isDatePicker={isDatePicker}
                            dateState={dateState}
                            setDateState={setDateState}
                            timePeriod={datePeriod}
                            handleChangeTimePeriod={setDatePeriod}
                            handleChangeTimeOrDatePicker={handleChangeTimeOrDatePicker}
                        />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap>
                        <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                            <TableNumberOfItemsBox
                                data={table.getFilteredRowModel().rows}
                                isLoading={meetingServiceResult.isLoading}
                                title="Number of orders"
                            />
                        </LocalizedStrict>

                        <TableTotalAmountBox
                            data={table.getFilteredRowModel().rows}
                            isLoading={meetingServiceResult.isLoading}
                            accessor={(row) => row.original.amount}
                        />
                    </TableSummaryWrap>
                )}
                state={{
                    rowSelection,
                }}
            />

            {showSuccessDialog.open && (
                <StandardSuccessDialog
                    dialogType={showSuccessDialog.type}
                    open={showSuccessDialog.open}
                    ordersCount={showSuccessDialog.ordersCount}
                    onClose={async () => {
                        setShowSuccessDialog({ open: false })
                        await queryClient.invalidateQueries({
                            queryKey: [UpcomingOrdersMeetingService.UPCOMING_ORDERS_MEETING_SERVICE],
                        })
                    }}
                />
            )}

            {chatDialogOrder !== null && (
                <OrderChatDrawer
                    open
                    onClose={() => setChatDialogOrder(null)}
                    orderId={chatDialogOrder.id}
                    orderTitle={chatDialogOrder.title}
                    viewerRole={ChatMessageAuthorRole.CUSTOMER}
                    sender={userId && companyId ? { type: UserType.USER, companyId, userId } : undefined}
                    onMessageSent={() =>
                        queryClient.invalidateQueries({
                            queryKey: [UpcomingOrdersMeetingService.UPCOMING_ORDERS_MEETING_SERVICE],
                        })
                    }
                    onSubtitleClick={() => setReceiptOrderId(chatDialogOrder.id)}
                />
            )}

            {receiptOrderId !== null && (
                <UserMeetingsReceipt
                    open
                    id={receiptOrderId}
                    meetings={getFilteredTableData(tableInstanceRef)}
                    onClose={() => setReceiptOrderId(null)}
                />
            )}
        </>
    )
}
